import { useEffect, useRef, useState, useCallback } from 'react'
import AOS from 'aos'
import { SITE,ROUTES } from '@/constants'
import Button from '@ui/Button'
import Breadcrumb from '@common/Breadcrumb'
import { useRecaptcha } from '@helpers/useRecaptcha'
import { MAIL_API_URL, buildCareerEmailBody, readEnquiryStatus } from '@/helpers/sendMail'
import MetaInfo from '@common/MetaInfo'
import { metaTitleFor } from '../assets/js/navBreadcrumb'
const { label, pageDesc, metaDesc } = metaTitleFor(ROUTES.APPLY_FOR_JOB)

/* ── Hiring-process SVG icons ── */
const iconBaseProps = {
  viewBox: '0 0 48 48',
  'aria-hidden': 'true',
  focusable: 'false',
}

function ApplyOnlineIcon({ className }) {
  return (
    <svg className={className} {...iconBaseProps}>
      <rect x="11" y="5" width="22" height="30" rx="3" className="icon-tone-soft" />
      <rect x="16" y="15" width="12" height="2.2" rx="1.1" className="icon-tone-muted" />
      <rect x="16" y="20" width="12" height="2.2" rx="1.1" className="icon-tone-muted" />
      <rect x="16" y="25" width="8"  height="2.2" rx="1.1" className="icon-tone-muted" />
      <circle cx="33" cy="33" r="9" className="icon-tone-accent" />
      <path d="M29.5 33.3l2.4 2.4 5.6-5.9" className="icon-tone-white-stroke"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function HrScreeningIcon({ className }) {
  return (
    <svg className={className} {...iconBaseProps}>
      <circle cx="18" cy="14" r="6" className="icon-tone-soft" />
      <path d="M6 34c0-7.2 5.4-12 12-12s12 4.8 12 12" className="icon-tone-soft" />
      <circle cx="31" cy="30" r="7" className="icon-tone-primary-stroke" strokeWidth="2.4" />
      <line x1="36.2" y1="35.2" x2="41" y2="40"
        className="icon-tone-primary-stroke" strokeWidth="2.4" strokeLinecap="round" />
    </svg>
  )
}

function TechAssessmentIcon({ className }) {
  return (
    <svg className={className} {...iconBaseProps}>
      <rect x="8" y="10" width="32" height="20" rx="2" className="icon-tone-soft" />
      <rect x="8" y="10" width="32" height="20" rx="2"
        className="icon-tone-primary-stroke" strokeWidth="1.6" />
      <path d="M18 17l-4 5 4 5" className="icon-tone-accent-stroke"
        strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M30 17l4 5-4 5" className="icon-tone-accent-stroke"
        strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="6" y="32" width="36" height="4" rx="2" className="icon-tone-primary" />
    </svg>
  )
}

function FinalInterviewIcon({ className }) {
  return (
    <svg className={className} {...iconBaseProps}>
      <path d="M6 12a4 4 0 0 1 4-4h14a4 4 0 0 1 4 4v10a4 4 0 0 1-4 4H14l-6 5v-5a4 4 0 0 1-2-3z"
        className="icon-tone-soft" />
      <path d="M20 22a4 4 0 0 1 4-4h14a4 4 0 0 1 4 4v8a4 4 0 0 1-4 4h-2l1 5-6-5h-7a4 4 0 0 1-4-4z"
        className="icon-tone-accent" />
    </svg>
  )
}

function OfferOnboardingIcon({ className }) {
  return (
    <svg className={className} {...iconBaseProps}>
      <path d="M15 26l-4 14 9-5 9 5-4-14" className="icon-tone-accent" />
      <circle cx="24" cy="18" r="12" className="icon-tone-soft" />
      <path d="M18 18.5l4 4 8-8.5" className="icon-tone-primary-stroke"
        strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

const HIRING_STEPS = [
  { number: '01', title: 'Apply Online',        Icon: ApplyOnlineIcon,     desc: 'Submit your resume and application through this page. We review every application personally.' },
  { number: '02', title: 'HR Screening',        Icon: HrScreeningIcon,     desc: 'A brief call to understand your background, expectations, and interest in the role.' },
  { number: '03', title: 'Technical Assessment',Icon: TechAssessmentIcon,  desc: 'A practical coding or design challenge tailored to the role — usually 60–90 minutes to assess your skills.' },
  { number: '04', title: 'Final Interview',     Icon: FinalInterviewIcon,  desc: 'Meet the hiring manager. Discuss your work profile, your salary, and your future here.' },
  { number: '05', title: 'Offer & Onboarding', Icon: OfferOnboardingIcon, desc: 'Receive your offer letter and begin your journey with CruxBytes.' },
]

/* ── Form constants ── */
const APPLYING_FOR_OPTIONS = [
  'Web Developer (Full Stack / Back End / Front End)',
  'Mobile App Developer',
  'Web Designer / UI-UX Designer',
  'Internship-IT',
  'Project Manager',
  'Business Analyst',
  'Business Development Executive',
  'IT Sales/Pre-Sales',
  'Internship-Sales',
  'Digital Marketing',    
  'Internship-Marketing',
]
const HEARD_ABOUT_OPTIONS = [
  'Friend / Relative', 'Search portal', 'Social website',
  'News / Ad / Blog / Post', 'Seminar / Conference', 'Any other',
]

const INIT = { name: '', email: '', mobile: '', applyingFor: '', heardAbout: '', resumeUrl: '' }

function validate(form) {
  const errors = {}
  if (!form.name.trim())   errors.name        = 'Name is required'
  if (!form.mobile.trim()) errors.mobile      = 'Mobile number is required'
  if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
                           errors.email       = 'Invalid email address'
  if (!form.applyingFor)   errors.applyingFor = 'Please select a role'
  return errors
}


const MAIL_SUBJECT = 'Job Enquiry | <ROLE>'
/* ── Page ── */
export default function CareerPage() {
 
  //useEffect(() => { AOS.refresh(); window.scrollTo(0, 0) }, [])
 

     const [form,           setForm          ] = useState(INIT)
  const [resumeFile,     setResumeFile    ] = useState(null)
  const [captchaToken,   setCaptchaToken  ] = useState('')
  const [pendingSubmit,  setPendingSubmit ] = useState(false)
  const [errors,         setErrors        ] = useState({})
  const [status,         setStatus        ] = useState('idle')
   useEffect(() => {
  const result = readEnquiryStatus()
  if (result) setStatus(result)
}, [])

  const formRef = useRef(null)
  const { execute: executeRecaptcha } = useRecaptcha()

  const handleChange = e => {
    const { name, value } = e.target
    setForm(f => ({ ...f, [name]: value }))
    if (errors[name]) setErrors(er => ({ ...er, [name]: '' }))
  }
  const handleFileChange = e => {
    setResumeFile(e.target.files?.[0] || null)
    if (errors.resume) setErrors(er => ({ ...er, resume: '' }))
  }
// v3 runs invisibly with no widget/challenge — execute() resolves directly
// with a token. Submission waits for it before the native POST fires
// (fetch can't be used here: no CORS on the mail-sending API).
const handleSubmit = async (e) => {
  e.preventDefault()

  const errs = validate(form)
  if (Object.keys(errs).length) {
    setErrors(errs)
    return
  }
  setStatus('loading')
  try {
    const token = await executeRecaptcha('career_application')
    setCaptchaToken(token)
    setPendingSubmit(true)
  } catch {
    setStatus('idle')
    setErrors(er => ({ ...er, recaptcha: 'Verification failed. Please try again.' }))
  }
}

useEffect(() => {
  if (pendingSubmit && captchaToken) {
    setPendingSubmit(false)
    formRef.current?.submit()
  }
}, [pendingSubmit, captchaToken])

  return (
    <>
    {/* <MetaInfo
        title="Apply for Job | CruxBytes"
        description="Join the CruxBytes team. Explore career opportunities in software development, design, project management, sales and marketing, and submit your CV online."
        keywords="careers, jobs, apply for job, software developer jobs, UI UX designer jobs, internship, IT sales jobs, CruxBytes careers"
      /> */}
       <MetaInfo
     title={label} 
      description={metaDesc}
      keywords=""
      />
    <main id="main-content" className="page-wrapper">

      {/* ── Hero ── */}
      <div className="page-hero page-hero-small">
        <div className="bg-grid bg-grid--fill" aria-hidden="true" />
        <div className="container">
          {/* <div className="section-label section-label-small">- Careers -</div> */}
            <Breadcrumb />
          <h1 data-aos="fade-up" data-aos-delay={100}>
            {/* Apply for Job */}
            {label}
            </h1>
          <p className="page-subtitle page-subtitle-small" data-aos="fade-up" data-aos-delay={200}>
            {/* Unlock your potential, explore challenging opportunities and make yourself shine amongst the growing technological challenges. Come, join our team and feel the difference. */}
            {pageDesc}
          </p>
                    
        </div>
      </div>

      {/* ── Main: infographic + form ── */}
      <div className="section-py bg-linear-gradient-light">
        <div className="container career-split">

          {/* ── LEFT: hiring process infographic ── */}
 <div className="cp-timeline" data-aos="fade-right" aria-label="Our hiring process">

  <div className="cp-timeline-header">
    <h2 >Our Hiring Process</h2>
    <p >
     A few straightforward steps from application to your first day.
    </p>
  </div>

 <div className="cp-vline-track" aria-label="Hiring process steps">

  {/* Vertical line — purely decorative */}
  <div className="cp-vline" aria-hidden="true" />

  {HIRING_STEPS.map((step, idx) => (
    <div
      key={step.number}
      className="cp-vline-step"
      data-aos="fade-up"
      data-aos-delay={idx * 80}
    >
      {/* Numbered circle badge */}
     {/* Diamond badge — number via data-number, counter-rotated in CSS */}
<div
  className="cp-vline-badge"
  data-number={step.number}
  aria-hidden="true"
/>

      {/* Title + description */}
      <div className="cp-vline-content">
        <h3 className="cp-vline-title">{step.title}</h3>
        <p className="cp-vline-desc">{step.desc}</p>
      </div>
    </div>
  ))}

</div>

</div>

          {/* ── RIGHT: application form ── */}
          <div id="apply-form" className="cp-form-wrap" data-aos="fade-left">
            
              
            
            

            <div className="contact-form-wrap career-form-wrap">
                <p style={{paddingBottom:'1.2rem'}}>
                  <b>Submit your CV, we will contact you as soon as we have relevant openings</b></p>
              {status === 'success' ? (
                <div className="contact-success" role="alert">
                  <i className="fa-solid fa-circle-check" />
                  <h3>Application Received!</h3>
                  <p>Thank you — our team will review your details and get back to you shortly.</p>
                  <button
                    type="button"
                    className="cb-btn cb-btn-secondary cb-btn-sm"
                    onClick={() => setStatus('idle')}
                  >
                    Submit Another
                  </button>
                </div>
              ) : (
                <form
  ref={formRef}
  onSubmit={handleSubmit}
  action={MAIL_API_URL}
  method="POST"
   
  className="contact-form"
  noValidate
  aria-label="Job application form"
>
  {/* Hidden fields */}
  <input type="hidden" name="EmailTo"   value={form.email} />
  <input type="hidden" name="EmailBcc"  value={SITE.mail_bcc} />
  <input type="hidden" name="Subject"   value={MAIL_SUBJECT} />
  <input type="hidden" name="EmailBody" value={buildCareerEmailBody(form)} />
  <input type="hidden" name="name"      value={form.name} />
  <input type="hidden" name="email"     value={form.email} />
  <input type="hidden" name="contact"   value={form.mobile} />
  <input type="hidden" name="ReturnUrl" value={ROUTES.APPLY_FOR_JOB} />
  <input type="hidden" name="filename" value={form.resumeUrl} />
  <input type="hidden" name="source" value="apply-for-job" />
  <input type="hidden" name="EmailAlias"   value={SITE.emailAlias} />
<input type="hidden" name="eMailSenderIDFrom"   value={SITE.eMailSenderIDFrom} />
                  <div className="form-field">
                    <label htmlFor="name" className="form-label">
                      Your Name <span className="form-req" aria-hidden="true">*</span>
                    </label>
                    <input id="name" name="name" type="text" placeholder="Your full name"
                      value={form.name} onChange={handleChange}
                      className={`form-input${errors.name ? ' form-input-error' : ''}`}
                      aria-required="true" aria-invalid={!!errors.name}
                      aria-describedby={errors.name ? 'err-name' : undefined}
                      autoComplete="name" />
                    {errors.name && <span id="err-name" role="alert" className="form-err-msg">{errors.name}</span>}
                  </div>

                  <div className="form-field">
                    <label htmlFor="email" className="form-label">Email Id</label>
                    <input id="email" name="email" type="email" placeholder="you@example.com"
                      value={form.email} onChange={handleChange}
                      className={`form-input${errors.email ? ' form-input-error' : ''}`}
                      aria-invalid={!!errors.email}
                      aria-describedby={errors.email ? 'err-email' : undefined}
                      autoComplete="email" />
                    {errors.email && <span id="err-email" role="alert" className="form-err-msg">{errors.email}</span>}
                  </div>

                  <div className="form-field">
                    <label htmlFor="mobile" className="form-label">
                      Mobile Number <span className="form-req" aria-hidden="true">*</span>
                    </label>
                    <input id="mobile" name="mobile" type="tel" placeholder="+91 XXXXX XXXXX"
                      value={form.mobile} onChange={handleChange}
                      className={`form-input${errors.mobile ? ' form-input-error' : ''}`}
                      aria-required="true" aria-invalid={!!errors.mobile}
                      aria-describedby={errors.mobile ? 'err-mobile' : undefined}
                      autoComplete="tel" />
                    {errors.mobile && <span id="err-mobile" role="alert" className="form-err-msg">{errors.mobile}</span>}
                  </div>

                  <div className="form-field">
                    <label htmlFor="applyingFor" className="form-label">
                      Applying For <span className="form-req" aria-hidden="true">*</span>
                    </label>
                    <select id="applyingFor" name="applyingFor"
                      value={form.applyingFor} onChange={handleChange}
                      className={`form-input${errors.applyingFor ? ' form-input-error' : ''}`}
                      aria-required="true" aria-invalid={!!errors.applyingFor}>
                      <option value="">-- Select --</option>
                      {APPLYING_FOR_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                    {errors.applyingFor && <span role="alert" className="form-err-msg">{errors.applyingFor}</span>}
                  </div>

                  <div className="form-field">
                    <label htmlFor="resumeUrl" className="form-label">
                      Upload Resume (.pdf / .docx) <span className="form-req" aria-hidden="true">*</span>
                    </label>
                    {/* <input
    id="resumeUrl"
    name="resumeUrl"
    type="url"
    placeholder="Paste your Google Drive / Dropbox resume link"
    value={form.resumeUrl}
    onChange={handleChange}
    className={`form-input${errors.resumeUrl ? ' form-input-error' : ''}`}
    aria-required="true"
  />
  <small className="form-hint">Upload your resume to Google Drive and paste the shareable link here</small>
  {errors.resumeUrl && <span role="alert" className="form-err-msg">{errors.resumeUrl}</span>} */}
   <input id="resumeUrl" name="resumeUrl" type="file" accept=".pdf,.docx"
                      onChange={handleFileChange}
                      className={`form-input${errors.resumeUrl ? ' form-input-error' : ''}`}
                      aria-required="true" aria-invalid={!!errors.resume}
                      aria-describedby={errors.resume ? 'err-resume' : undefined} />
                    {errors.resumeUrl && <span id="err-resume" role="alert" className="form-err-msg">{errors.resumeUrl}</span>}
          
                  </div>

                  <div className="form-field">
                    <label htmlFor="heardAbout" className="form-label">How Did You Hear About Us</label>
                    <select id="heardAbout" name="heardAbout"
                      value={form.heardAbout} onChange={handleChange}
                      className="form-input">
                      <option value="">-- Select --</option>
                      {HEARD_ABOUT_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </div>

                                   {/* reCAPTCHA v3 — no visible widget; token travels via hidden field */}
                  <input type="hidden" name="g-recaptcha-response" value={captchaToken} />
                  {errors.recaptcha && (
                    <div className="form-field">
                      <span role="alert" className="form-err-msg">{errors.recaptcha}</span>
                    </div>
                  )}
{status === 'error' && (
  <p role="alert" aria-live="assertive" className="form-status form-status-error">
    Sorry, we couldn't send your application. Please try again or contact us directly.
  </p>
)}
                  <button
                    type="submit"
                    className="cb-btn cb-btn-primary cb-btn-md"                    
                    disabled
                    style={{width:'15rem'}}
                  >
                    {status === 'loading' ? 'Submitting…' : 'Submit Application'}
                  </button>
                  <p>Currently unavailable</p>
                </form>
              )}
            </div>
          </div>

        </div>
      </div>
    </main>
    </>
  )
}